import { useState } from 'react';

const BASE = process.env.REACT_APP_API_URL || '/api';

/* Sentinel error class so callers can distinguish daily-limit from other errors */
export class DailyLimitError extends Error {
  constructor(message) {
    super(message);
    this.name = 'DailyLimitError';
  }
}

/* ── Core GPT-4 call via backend proxy ─────────────────────────────────── */
const callGPT4 = async (prompt, system = '', max_tokens = 2048) => {
  const token = localStorage.getItem('rb_token');
  const res = await fetch(`${BASE}/ai/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      messages: [{ role: 'user', content: prompt }],
      system_prompt: system,
      temperature: 0.7,
      max_tokens,
    }),
  });

  const data = await res.json();
  if (!res.ok) {
    if (data.error === 'daily_limit_exceeded') throw new DailyLimitError(data.message || 'Daily AI limit reached.');
    throw new Error(data.error || 'AI request failed');
  }
  return data.result || '';
};

/* Strip markdown code fences if the model wraps JSON in them */
const stripFences = (raw) => raw.replace(/```json\s*/gi, '').replace(/```\s*/g, '').trim();

const parseJSON = (raw) => {
  const cleaned = stripFences(raw);
  /* Try full parse first, then extract first {...} or [...] block */
  try { return JSON.parse(cleaned); } catch (_) {}
  const objMatch = cleaned.match(/(\{[\s\S]*\})/);
  if (objMatch) { try { return JSON.parse(objMatch[1]); } catch (_) {} }
  const arrMatch = cleaned.match(/(\[[\s\S]*\])/);
  if (arrMatch) { try { return JSON.parse(arrMatch[1]); } catch (_) {} }
  throw new Error('Could not parse AI response as JSON');
};

/* ── Hook ───────────────────────────────────────────────────────────────── */
export const useClaudeAI = () => {
  const [loading,          setLoading]          = useState(false);
  const [error,            setError]            = useState(null);
  const [dailyLimitError,  setDailyLimitError]  = useState(false);
  const [sectionLoading,   setSectionLoading]   = useState(null);

  const run = async (fn) => {
    setLoading(true);
    setError(null);
    try {
      return await fn();
    } catch (e) {
      if (e instanceof DailyLimitError) {
        setDailyLimitError(true);
      } else {
        setError(e.message);
      }
      throw e;
    } finally {
      setLoading(false);
    }
  };

  /* ── Generate full resume / CV / cover letter ─────────────────────────── */
  const generateResume = (input, docType = 'resume') => run(async () => {
    const system = 'You are an expert career coach. Return ONLY valid JSON, no markdown fences, no extra text.';
    const isCV = docType === 'cv';
    const isCL = docType === 'cover_letter';

    if (isCL) {
      const raw = await callGPT4(
        `Generate a professional cover letter JSON for: ${JSON.stringify(input)}\n\nReturn ONLY this JSON:\n{"personalInfo":{"fullName":"","email":"","phone":"","location":""},"jobInfo":{"company":"","role":"","hiringManager":""},"content":{"opening":"compelling opening paragraph","body":"two strong body paragraphs separated by newline","closing":"strong closing paragraph"}}`,
        system, 2048,
      );
      return parseJSON(raw);
    }

    const raw = await callGPT4(
      `Generate a professional ${isCV ? 'academic CV' : 'resume'} JSON for:\n${JSON.stringify(input)}\n\nReturn ONLY this JSON structure:\n{"personalInfo":{"fullName":"","jobTitle":"","email":"","phone":"","location":"","linkedin":"","website":"","summaryTitle":"Professional Summary","summary":"3-4 sentence summary","dateOfBirth":"","nationality":"","sex":"","showDOB":false,"showNationality":false,"showSex":false},"experience":[{"id":"1","company":"","title":"","location":"","startDate":"YYYY-MM","endDate":"","current":true,"description":"role overview","bullets":["achievement 1","achievement 2","achievement 3"]}],"education":[{"id":"1","school":"","degree":"","location":"","startDate":"YYYY-MM","endDate":"YYYY-MM","gpa":""}],"skills":[{"id":"1","category":"Category","items":"skill1, skill2"}],"projects":[{"id":"1","title":"","date":"YYYY-MM","summary":"","bullets":[],"link":"","skills":""}],"certifications":[{"id":"1","name":"","issuer":"","date":"YYYY-MM"}],"languages":[{"id":"1","language":"","proficiency":""}],"achievements":[{"id":"1","title":"","description":"","date":"YYYY-MM"}],"customBlocks":[]}`,
      system, 3000,
    );
    return parseJSON(raw);
  });

  /* ── Improve a text field ─────────────────────────────────────────────── */
  const improveText = (text, ctx) => run(() =>
    callGPT4(
      `Improve this ${ctx} to be more impactful and professional. Return ONLY the improved text, nothing else:\n\n${text}`,
      'You are an expert career coach. Return only the improved text, no explanations, no quotes.',
      512,
    ),
  );

  /* ── Generate bullet points for an experience entry ──────────────────── */
  const generateBullets = (role, company, desc) => run(async () => {
    const raw = await callGPT4(
      `Generate 3 powerful, metrics-driven resume bullet points for ${role} at ${company}.${desc ? ' Context: ' + desc : ''}\nReturn ONLY a JSON array with exactly 3 strings: ["bullet 1","bullet 2","bullet 3"]`,
      'You are an expert resume writer. Return only a JSON array of 3 strings.',
      512,
    );
    return parseJSON(raw);
  });

  /* ── Tailor resume / cover letter to a job description ───────────────── */
  const tailorResume = (resumeData, jobDesc) => run(async () => {
    const isCL = resumeData?.type === 'cover_letter';
    const prompt = isCL
      ? `Tailor this cover letter for the job below. Rewrite opening, body, and closing to match the role requirements. Keep same JSON structure.\n\nCover Letter JSON:\n${JSON.stringify(resumeData)}\n\nJob Description:\n${jobDesc}\n\nReturn ONLY the updated cover letter JSON.`
      : `Tailor this resume for the job. Adjust summary, reorder/enhance skills, emphasize relevant experience. Keep same JSON structure.\n\nResume JSON:\n${JSON.stringify(resumeData)}\n\nJob Description:\n${jobDesc}\n\nReturn ONLY the updated resume JSON.`;
    const raw = await callGPT4(prompt, 'You are an expert career coach. Return ONLY valid JSON, no markdown fences.', 3000);
    return parseJSON(raw);
  });

  /* ── Generate interview questions ────────────────────────────────────── */
  const generateInterviewQuestions = ({ jobTitle, seniority, mode, jobAd, resumeData }) => run(async () => {
    const context = mode === 'personalized' && jobAd
      ? `\n\nJob Ad:\n${jobAd}${resumeData ? `\n\nCandidate Resume Summary:\n${JSON.stringify(resumeData?.personalInfo?.summary || '')}` : ''}`
      : '';

    const prompt = `Generate 5 interview questions and 3 follow-up questions for a ${seniority}-level ${jobTitle} position.${context}

Return ONLY this exact JSON:
{
  "questions": [
    {
      "question": "Full question text",
      "hint": "This question tests your ability to ...",
      "answer": "Sample answer using **bold keywords** across 2-3 sentences.",
      "answerHint": "The answer demonstrates ...",
      "type": "interview"
    }
  ]
}
First 5 items type="interview", last 3 items type="followup". Follow-ups dig deeper into the interview answers.`;

    const raw = await callGPT4(prompt, 'You are an expert interview coach. Return ONLY valid JSON, no markdown fences.', 2500);
    return parseJSON(raw);
  });

  /* ── Generate one adaptive follow-up question based on Q&A so far ────── */
  const generateFollowUpQuestion = ({ jobTitle, previousQA, questionNumber, totalQuestions }) => run(async () => {
    const context = previousQA
      .map((qa, i) => `Q${i + 1}: ${qa.question}\nCandidate: ${qa.answer}`)
      .join('\n\n');

    const raw = await callGPT4(
      `You are an expert interviewer conducting a job interview for a ${jobTitle} position. This is question ${questionNumber} of ${totalQuestions}.

Previous interview exchange:
${context}

Generate ONE natural follow-up question that:
- Builds on something specific the candidate just mentioned
- Digs deeper into their skills, experience, or problem-solving
- Is relevant to the ${jobTitle} role
- Has NOT been asked before

Return ONLY: {"question": "The full question?", "hint": "What this question assesses"}`,
      'You are an expert interviewer. Return ONLY valid JSON, no markdown fences.',
      350,
    );
    return parseJSON(raw);
  });

  /* ── Real-time answer analysis → score + next-action recommendation ─── */
  const analyzeInterviewResponse = ({ question, answer, jobTitle, seniority, interviewHistory }) => run(async () => {
    const prevCtx = interviewHistory.length > 1
      ? '\n\nPrevious exchange (last 3 turns):\n' + interviewHistory.slice(-4, -1)
          .map(h => `Q: ${h.question}\nA: ${h.answer}`).join('\n\n')
      : '';

    const raw = await callGPT4(
      `Evaluate this candidate answer for a ${seniority || 'mid'}-level ${jobTitle} interview.

Question: "${question}"
Answer: """${answer || '(No answer given)'}"""${prevCtx}

Return ONLY this JSON:
{
  "score": 7,
  "strengths": ["one specific strength"],
  "weaknesses": ["one specific gap or missing element"],
  "confidenceLevel": 75,
  "completeness": 80,
  "communicationScore": 70,
  "technicalScore": 65,
  "recommendation": "next",
  "reasoning": "One sentence reason for the recommendation"
}
score: 0-10 integer. Other metrics: 0-100 integers.
recommendation: "clarify" = answer is vague or missing key specifics, ask for elaboration. "followup" = solid answer that reveals a thread worth exploring deeper. "next" = complete answer, move to next topic.`,
      'You are an expert interview evaluator. Return ONLY valid JSON, no markdown fences.',
      500,
    );
    const p = parseJSON(raw);
    return {
      score:             Math.max(0, Math.min(10,  Math.round(Number(p.score))              || 0)),
      strengths:         Array.isArray(p.strengths)  ? p.strengths.map(String)              : [],
      weaknesses:        Array.isArray(p.weaknesses) ? p.weaknesses.map(String)             : [],
      confidenceLevel:   Math.max(0, Math.min(100, Math.round(Number(p.confidenceLevel))    || 50)),
      completeness:      Math.max(0, Math.min(100, Math.round(Number(p.completeness))       || 50)),
      communicationScore: Math.max(0, Math.min(100, Math.round(Number(p.communicationScore)) || 50)),
      technicalScore:    Math.max(0, Math.min(100, Math.round(Number(p.technicalScore))     || 50)),
      recommendation:    ['clarify', 'followup', 'next'].includes(p.recommendation) ? p.recommendation : 'next',
      reasoning:         typeof p.reasoning === 'string' ? p.reasoning : '',
    };
  });

  /* ── Detect if candidate asked something instead of answering ────────── */
  const classifyCandidateInterjection = ({ jobTitle, currentQuestion, utterance }) => run(async () => {
    const raw = await callGPT4(
      `You are an AI interviewer conducting a video interview for a ${jobTitle} position. You just asked the candidate:
"${currentQuestion}"

The candidate said:
"${utterance}"

Decide whether the candidate is actually attempting to answer the question (even briefly or imperfectly), OR is instead asking you something / making a meta-comment instead of answering — e.g. asking about the interview process or format, reporting a technical/audio issue, asking you to repeat or clarify the question, asking you a question back, or otherwise going off-topic.

Return ONLY this JSON:
{"isQuestion": true, "reply": "A brief, natural, spoken reply (1-2 sentences) addressing what they said, as the interviewer would say it out loud, ending by inviting them back to the question."}

If they ARE attempting to answer the question, return exactly: {"isQuestion": false, "reply": ""}`,
      'You are an expert AI interviewer. Return ONLY valid JSON, no markdown fences.',
      250,
    );
    const p = parseJSON(raw);
    return {
      isQuestion: p.isQuestion === true,
      reply: typeof p.reply === 'string' ? p.reply : '',
    };
  });

  /* ── Generate adaptive next question (clarify / followup / next topic) ── */
  const generateAdaptiveQuestion = ({ jobTitle, seniority, interviewHistory, blueprint, questionType }) => run(async () => {
    const histCtx = interviewHistory
      .map((h, i) => `Q${i + 1} [${h.type || 'main'}]: ${h.question}\nCandidate: ${h.answer}`)
      .join('\n\n');
    const lastAnswer = (interviewHistory.at?.(-1)?.answer || '').slice(0, 150);
    const topics = blueprint?.plannedTopics || ['Technical skills', 'Problem solving', 'Leadership', 'Communication', 'Career goals'];

    const instructions = {
      clarify:  'Generate ONE short clarification question (1 sentence). Ask for a specific example, measurable outcome, or missing detail from the last answer. Sound like a natural interviewer, not robotic.',
      followup: `Generate ONE follow-up question that references something specific from: "${lastAnswer}". Dig into an interesting detail they mentioned.`,
      next:     `Generate ONE new main interview question on a DIFFERENT topic. Available topics: ${topics.join(', ')}. Do NOT repeat any topic already covered. Make it specific to a ${seniority || 'mid'}-level ${jobTitle}.`,
    };

    const raw = await callGPT4(
      `You are a hiring manager conducting a ${seniority || 'mid'}-level ${jobTitle} interview.

Interview so far:
${histCtx || '(First question just asked)'}

Task: ${instructions[questionType] || instructions.next}

Return ONLY: {"question": "Full question text?", "hint": "What this assesses", "type": "${questionType}"}`,
      'You are an expert interviewer. Return ONLY valid JSON, no markdown fences.',
      280,
    );
    return parseJSON(raw);
  });

  /* ── Generate comprehensive final interview report ────────────────────── */
  const generateFinalInterviewReport = ({ jobTitle, seniority, interviewHistory, communicationMetrics }) => run(async () => {
    const transcript = interviewHistory
      .map((h, i) => {
        const sc = h.analysis?.score !== undefined ? ` (score: ${h.analysis.score}/10)` : '';
        return `Q${i + 1} [${h.type || 'main'}]${sc}: ${h.question}\nCandidate: ${h.answer}`;
      })
      .join('\n\n');

    const commNote = communicationMetrics?.answers > 0
      ? `\nSpeaking data: ${communicationMetrics.totalWords} total words across ${communicationMetrics.answers} answers, ${communicationMetrics.fillerWords} filler words detected`
      : '';

    const raw = await callGPT4(
      `You are a senior hiring manager. Write the final evaluation report for this ${seniority || 'mid'}-level ${jobTitle} interview.

Full transcript:
${transcript}${commNote}

Return ONLY this JSON:
{
  "overallScore": 72,
  "technicalScore": 75,
  "communicationScore": 68,
  "confidenceScore": 70,
  "behavioralScore": 65,
  "hiringLikelihood": "moderate",
  "strongestAnswer": "One sentence: which question they answered best and why it stood out",
  "weakestAnswer": "One sentence: which question was weakest and what was missing",
  "strengths": ["key strength 1", "key strength 2", "key strength 3"],
  "improvementAreas": ["improvement area 1", "improvement area 2"],
  "recommendedNextSteps": ["specific actionable step 1", "specific step 2", "specific step 3"],
  "summary": "2-3 sentence holistic assessment of the candidate"
}
All scores 0-100. hiringLikelihood: "strong" | "moderate" | "weak" | "not recommended"`,
      'You are a senior hiring manager writing a candidate evaluation. Return ONLY valid JSON.',
      900,
    );
    const p = parseJSON(raw);
    const c = (v, d = 50) => Math.max(0, Math.min(100, Math.round(Number(v)) || d));
    return {
      overallScore:      c(p.overallScore),
      technicalScore:    c(p.technicalScore),
      communicationScore: c(p.communicationScore),
      confidenceScore:   c(p.confidenceScore),
      behavioralScore:   c(p.behavioralScore),
      hiringLikelihood:  ['strong', 'moderate', 'weak', 'not recommended'].includes(p.hiringLikelihood) ? p.hiringLikelihood : 'moderate',
      strongestAnswer:   typeof p.strongestAnswer  === 'string' ? p.strongestAnswer  : '',
      weakestAnswer:     typeof p.weakestAnswer    === 'string' ? p.weakestAnswer    : '',
      strengths:         Array.isArray(p.strengths)             ? p.strengths.map(String)             : [],
      improvementAreas:  Array.isArray(p.improvementAreas)      ? p.improvementAreas.map(String)      : [],
      recommendedNextSteps: Array.isArray(p.recommendedNextSteps) ? p.recommendedNextSteps.map(String) : [],
      summary:           typeof p.summary === 'string' ? p.summary : '',
    };
  });

  /* ── Evaluate a user's practice answer to an interview question ──────── */
  const evaluateInterviewAnswer = ({ question, hint, userAnswer, jobTitle, seniority }) => run(async () => {
    const prompt = `You are evaluating a candidate's answer in a mock interview for a ${seniority}-level ${jobTitle} position.

Question: ${question}
${hint ? `What this question tests: ${hint}` : ''}

Candidate's answer:
"""
${userAnswer}
"""

Score it honestly and critically. If the question is behavioral ("Tell me about a time...", teamwork, conflict, failure), judge structure against the STAR method (Situation, Task, Action, Result) and make the suggested answer STAR-structured.

Return ONLY this exact JSON:
{
  "score": 7,
  "weightage": "high",
  "strengths": ["specific strength", "..."],
  "improvements": ["specific, actionable improvement", "..."],
  "suggestedAnswer": "A stronger version of the candidate's own answer with **bold keywords**, 3-5 sentences."
}
score is an integer 0-10. weightage must be "high" if the question targets core role competencies or critical skills, "medium" for standard experience/situational questions, "low" for general background or soft-skill questions. 2-4 items each in strengths and improvements.`;

    const raw = await callGPT4(prompt, 'You are an expert interview coach. Return ONLY valid JSON, no markdown fences, no extra text.', 1200);
    const parsed = parseJSON(raw);
    return {
      score: Math.max(0, Math.min(10, Math.round(Number(parsed.score)) || 0)),
      weightage: ['high', 'medium', 'low'].includes(parsed.weightage) ? parsed.weightage : 'medium',
      strengths: Array.isArray(parsed.strengths) ? parsed.strengths.map(String) : [],
      improvements: Array.isArray(parsed.improvements) ? parsed.improvements.map(String) : [],
      suggestedAnswer: typeof parsed.suggestedAnswer === 'string' ? parsed.suggestedAnswer : '',
    };
  });

  /* ── Generate skills for a job title ─────────────────────────────────── */
  const generateSkillsFromTitle = (jobTitle) => run(async () => {
    const raw = await callGPT4(
      `Generate professional skills for a ${jobTitle}. Return ONLY a JSON array: [{"id":"1","category":"Category Name","items":"skill1, skill2, skill3"}]. Include 5-6 categories with 3-6 skills each.`,
      'You are an expert resume writer. Return only a JSON array, no other text.',
      1024,
    );
    return parseJSON(raw).map((s, i) => ({ ...s, id: String(Date.now() + i) }));
  });

  /* ── Generate strengths for a job title ──────────────────────────────── */
  const generateStrengthsFromTitle = (jobTitle) => run(async () => {
    const raw = await callGPT4(
      `List 8-10 key professional strengths and core competencies for a ${jobTitle}. Return ONLY a comma-separated list. Example: Leadership, Critical Thinking, Communication`,
      'You are an expert career coach. Return only a comma-separated list of strengths, no other text.',
      256,
    );
    return raw.trim().replace(/\.$/, '');
  });

  /* ── ATS analysis ─────────────────────────────────────────────────────── */
  const analyzeATS = (resumeData) => run(async () => {
    const pi = resumeData?.personalInfo || {};
    const exp  = (resumeData?.experience    || []).slice(0, 4);
    const edu  = (resumeData?.education     || []).slice(0, 3);
    const sk   = (resumeData?.skills        || []).slice(0, 6);
    const secs = Object.keys(resumeData || {}).filter(k => Array.isArray(resumeData[k]) && resumeData[k].length > 0);

    const system = [
      'You are an ATS (Applicant Tracking System) expert.',
      'When given a resume, analyze it and respond ONLY with a JSON object using these exact keys:',
      '  overall: number 0-100',
      '  categories: array of exactly 3 objects, each with: name (string), score (0-100), description (string), issues (string array), passed (string array)',
      '    The 3 category names must be exactly: "Content", "Structure", "Design"',
      '  suggestions: array of objects with: priority ("high"|"medium"|"low"), category (string), text (string)',
      '  strengths: string array (3-4 items)',
      '  weakestLink: string (one of the 3 category names)',
      'Give realistic, specific scores based on the actual resume content. Be critical and honest.',
      'No markdown fences, no explanation text — ONLY the raw JSON object.',
    ].join(' ');

    const user = [
      'Analyze this resume for ATS compatibility:',
      '',
      `Name: ${pi.fullName || 'N/A'} | Job Title: ${pi.jobTitle || 'N/A'}`,
      `Summary: ${pi.summary || 'MISSING — no summary provided'}`,
      `Contact: email=${pi.email || 'missing'} | phone=${pi.phone || 'missing'} | location=${pi.location || 'missing'} | linkedin=${pi.linkedin || 'missing'}`,
      '',
      'Experience:',
      exp.length ? exp.map(e => `  - ${e.title} at ${e.company}: ${(e.bullets || []).slice(0, 3).join('; ') || e.description || 'no details'}`).join('\n') : '  NONE',
      '',
      'Education:',
      edu.length ? edu.map(e => `  - ${e.degree} at ${e.school}${e.gpa ? ' GPA: ' + e.gpa : ''}`).join('\n') : '  NONE',
      '',
      'Skills:',
      sk.length ? sk.map(s => `  - ${s.category}: ${s.items}`).join('\n') : '  NONE',
      '',
      `Sections present: ${secs.join(', ') || 'none'}`,
    ].join('\n');

    const raw = await callGPT4(user, system, 1800);
    return parseJSON(raw);
  });

  /* ── Improve a specific resume section based on ATS feedback ─────────── */
  const improveSection = async (resumeData, sectionKey, issues) => {
    setSectionLoading(sectionKey);
    try {
      const pi = resumeData?.personalInfo || {};
      let currentContent, returnInstruction;

      if (sectionKey === 'summary') {
        currentContent = pi.summary || '';
        returnInstruction = 'Return ONLY the improved summary as a plain text string. No JSON, no quotes, no explanation.';
      } else if (sectionKey === 'skills') {
        currentContent = resumeData?.skills || [];
        returnInstruction = 'Return ONLY a JSON array: [{"id":"...","category":"...","items":"skill1, skill2, ..."}]. Keep all existing IDs and categories, only improve the items.';
      } else if (sectionKey === 'experience') {
        currentContent = (resumeData?.experience || []).map(e => ({
          id: e.id, title: e.title, company: e.company, bullets: e.bullets || [],
        }));
        returnInstruction = 'Return ONLY a JSON array: [{"id":"...","bullets":["improved bullet 1","improved bullet 2","improved bullet 3"]}]. Keep existing IDs. Rewrite bullets to be metrics-driven and ATS-optimized.';
      }

      const issueList = issues?.length
        ? issues.map((iss, i) => `${i + 1}. ${iss}`).join('\n')
        : 'General: add more ATS-relevant keywords, quantifiable metrics, and strong action verbs.';

      const raw = await callGPT4(
        `Fix these ATS issues in the "${sectionKey}" section:\n${issueList}\n\nCurrent ${sectionKey}:\n${JSON.stringify(currentContent, null, 2)}\n\n${returnInstruction}`,
        'You are an expert ATS-optimized resume writer. Be specific, metrics-driven, and keyword-rich. No markdown fences.',
        1400,
      );

      if (sectionKey === 'summary') return stripFences(raw).replace(/^["']|["']$/g, '').trim();
      return parseJSON(raw);
    } catch (e) {
      if (e instanceof DailyLimitError) setDailyLimitError(true);
      throw e;
    } finally {
      setSectionLoading(null);
    }
  };

  /* ── AI Resume Chat (contextual improvement) ─────────────────────────── */
  const chatWithResume = (messages, resumeData) => run(async () => {
    const system = `You are an expert career coach and resume writer. The user is working on their resume. Help them improve it with specific, actionable advice. Be concise and direct. Resume context: ${JSON.stringify(resumeData?.personalInfo || {})}`;
    const lastMessage = messages[messages.length - 1]?.content || '';
    const history = messages.slice(-6).map(m => `${m.role === 'user' ? 'User' : 'Assistant'}: ${m.content}`).join('\n');
    const raw = await callGPT4(
      history ? `Conversation so far:\n${history}\n\nRespond to the latest user message.` : lastMessage,
      system, 1024,
    );
    return raw;
  });

  /* ── LinkedIn profile analysis ────────────────────────────────────────── */
  const analyzeLinkedInProfile = (profileText) => run(async () => {
    const system = [
      'You are a LinkedIn profile optimization expert.',
      'When given pasted LinkedIn profile text, analyze it and respond ONLY with a JSON object using these exact keys:',
      '  overall: number 0-100',
      '  sections: array of exactly 5 objects, each with: id, name, score (0-100), issues (string array, specific problems found), passed (string array, things done well)',
      '    The 5 sections must use these exact id/name pairs in this order: headline/Headline, about/About, experience/Experience, skills/Skills, completeness/Completeness',
      '    "completeness" covers overall profile fullness — education, recommendations, certifications, activity, etc.',
      '  biggestGaps: string array (3-5 short, specific, actionable gaps, e.g. "No headline provided", "About section is missing")',
      '  summary: one short phrase describing overall quality, e.g. "Needs work", "Good foundation", "Strong profile"',
      'Give realistic, specific, critical scores based on the actual pasted content — do not be generous. If a section is missing entirely, score it low and say so.',
      'No markdown fences, no explanation text — ONLY the raw JSON object.',
    ].join(' ');

    const raw = await callGPT4(
      `Analyze this LinkedIn profile text:\n\n${profileText.slice(0, 8000)}`,
      system, 1800,
    );
    const p = parseJSON(raw);
    const c = (v, d = 0) => Math.max(0, Math.min(100, Math.round(Number(v)) || d));
    const DEFAULT_SECTIONS = ['headline', 'about', 'experience', 'skills', 'completeness'];
    const sections = DEFAULT_SECTIONS.map((id, i) => {
      const s = Array.isArray(p.sections) ? p.sections.find(x => x.id === id) || p.sections[i] : null;
      return {
        id,
        name: s?.name || id.charAt(0).toUpperCase() + id.slice(1),
        score: c(s?.score),
        issues: Array.isArray(s?.issues) ? s.issues.map(String) : [],
        passed: Array.isArray(s?.passed) ? s.passed.map(String) : [],
      };
    });
    return {
      overall: c(p.overall),
      sections,
      biggestGaps: Array.isArray(p.biggestGaps) ? p.biggestGaps.map(String).slice(0, 5) : [],
      summary: typeof p.summary === 'string' ? p.summary : '',
    };
  });

  /* ── LinkedIn line-by-line rewrites (Pro) ─────────────────────────────── */
  const generateLinkedInRewrites = (profileText, gaps = []) => run(async () => {
    const raw = await callGPT4(
      `Rewrite the headline, About section, and top experience bullet for this LinkedIn profile so it is more compelling, keyword-rich, and recruiter-friendly.\n\nKnown gaps to fix: ${gaps.join('; ') || 'none specified'}\n\nProfile text:\n${profileText.slice(0, 8000)}\n\nReturn ONLY this JSON:\n{"headline":"rewritten headline, under 220 characters","about":"rewritten About section, 3-5 short paragraphs","experienceBullet":"one rewritten, metrics-driven experience bullet based on the candidate's most recent role"}`,
      'You are a LinkedIn profile optimization expert and professional copywriter. Return ONLY valid JSON, no markdown fences.',
      1400,
    );
    const p = parseJSON(raw);
    return {
      headline: typeof p.headline === 'string' ? p.headline : '',
      about: typeof p.about === 'string' ? p.about : '',
      experienceBullet: typeof p.experienceBullet === 'string' ? p.experienceBullet : '',
    };
  });

  return {
    loading, error,
    dailyLimitError, clearDailyLimitError: () => setDailyLimitError(false),
    generateResume, improveText, generateBullets,
    tailorResume, generateInterviewQuestions, evaluateInterviewAnswer,
    generateSkillsFromTitle, generateStrengthsFromTitle,
    analyzeATS, chatWithResume,
    improveSection, sectionLoading,
    generateFollowUpQuestion,
    analyzeInterviewResponse,
    classifyCandidateInterjection,
    generateAdaptiveQuestion,
    generateFinalInterviewReport,
    analyzeLinkedInProfile, generateLinkedInRewrites,
  };
};
